import { Injectable } from '@angular/core';
import { CustomXmlPart } from '../interfaces/customxmlpart';
import { AbstractPartService } from '../utils/abstract-part.service';
import { Dom } from '../utils/dom';
import { FieldContext } from '../utils/fieldcontext';
import { Tag, TagType } from '../utils/tag';
import { XPath, XPathCondition } from '../utils/xpath-utils';
import { ApplicationStoreService, INamespacePart } from './application-store.service';
import { WordService } from './word.service';

@Injectable({ providedIn: 'root' })
export class DopePartService extends AbstractPartService {
  constructor(private wordService: WordService, private applicationStoreService: ApplicationStoreService) {
    super();
  }

  async create(namespace: string, modelId: string): Promise<{ xpathsPart: CustomXmlPart; conditionsPart: CustomXmlPart }> {
    const skeletonXml = `
      <od:xpaths xmlns:od="http://opendope.org/xpaths">
        <od:xpath id="i0">
          <od:dataBinding storeItemID="${modelId}" xpath="/${namespace}/kpInfo"/>
        </od:xpath>
      </od:xpaths>`;
    const xpathsPart = await this.wordService.createCustomXmlPart(skeletonXml.trim());
    const conditionsPart = await this.wordService.createCustomXmlPart(
      '<od:conditions xmlns:od="http://opendope.org/conditions"></od:conditions>'
    );
    return { xpathsPart, conditionsPart };
  }

  async add(part: INamespacePart, fieldContext: FieldContext, xpath: XPath, refId: number): Promise<string> {
    if (fieldContext.isImageField) {
      throw new Error('Image fields should not be added to the DoPE XML part.');
    }

    const refTag = this.makeRefTag(refId);
    await this.addToXPaths(part, xpath, refTag);

    if (fieldContext.isContainer) {
      return `kp:xpath=${refTag}`;
    }

    if (fieldContext.isIterable) {
      return `od:repeat=${refTag}`;
    }

    if (fieldContext.isConditionalField) {
      const conditionTag = await this.makeConditionalTag();
      await this.addToConditionalPart(part, refTag, conditionTag);
      return `od:condition=${conditionTag}`;
    }

    let tag = `od:xpath=${refTag}`;
    if (fieldContext.stringFormat?.renderHtml) {
      tag += '&od:ContentType=application/xhtml+xml';
    }
    return tag;
  }

  async hasPath(tag: Tag, part: INamespacePart): Promise<boolean> {
    if (tag.type === TagType.XPath || tag.type == TagType.Repeat) {
      const xpathsDom = await this.getDOM(part.dopeXPaths);
      return xpathsDom.findChildById(tag.id) != null;
    }

    if (tag.type === TagType.Condition) {
      const conditionsDom = await this.getDOM(part.dopeConditions);
      return conditionsDom.findChildById(tag.id) != null;
    }

    return false;
  }

  async getPath(tag: Tag, part: INamespacePart): Promise<string | null> {
    if (tag.type === TagType.XPath || tag.type === TagType.Repeat) {
      const xpathsDom = await this.getDOM(part.dopeXPaths);
      const element = xpathsDom.findChildById(tag.id);
      return element ? element.children[0].getAttribute('xpath') : null;
    }

    if (tag.type === TagType.Condition) {
      const conditionsDom = await this.getDOM(part.dopeConditions);
      const conditionElement = conditionsDom.findChildById(tag.id);
      if (conditionElement) {
        const refId = conditionElement.children[0].getAttribute('id');
        const xpathsDom = await this.getDOM(part.dopeXPaths);
        const element = xpathsDom.findChildById(refId!);
        return element ? element.children[0].getAttribute('xpath') : null;
      }
    }

    return null;
  }

  async removePath(tag: Tag, part: INamespacePart): Promise<void> {
    if (tag.type !== TagType.Condition) {
      await this.removeXPathElement(tag.id, part.dopeXPaths);
    } else {
      await this.removeConditionElement(tag.id, part.dopeConditions, part.dopeXPaths);
    }
  }

  private async removeXPathElement(id: string, dopeXPaths: CustomXmlPart): Promise<boolean> {
    const xpathsDom = await this.getDOM(dopeXPaths);
    const xpathElement = xpathsDom.findChildById(id);
    if (xpathElement) {
      xpathElement.remove();
      await dopeXPaths.setXml(xpathsDom.outerHTML);
    }
    return xpathElement !== undefined;
  }

  private async removeConditionElement(id: string, dopeConditions: CustomXmlPart, dopeXPaths: CustomXmlPart): Promise<boolean> {
    const conditionsDom = await this.getDOM(dopeConditions);
    const condElement = conditionsDom.findChildById(id);
    if (condElement) {
      const refId = condElement.children[0].getAttribute('id');
      await this.removeXPathElement(refId!, dopeXPaths);
      condElement.remove();
      await dopeConditions.setXml(conditionsDom.outerHTML);
    }

    return condElement !== undefined;
  }

  async countBindings(xpath: string | null, part: INamespacePart): Promise<number> {
    if (!xpath) {
      return 0;
    }
    const xpathsDom = await this.getDOM(part.dopeXPaths);
    const elements = xpathsDom.findDescendantsByAttribute('xpath', xpath);
    return elements.length;
  }

  private async addToXPaths(part: INamespacePart, xpath: XPath, refTag: string): Promise<void> {
    const doc = await this.getDOM(part.dopeXPaths);
    const nsPrefix = doc.documentElement.getAttribute('xmlns:odx') ? 'odx' : 'od';

    const pathElement = this.createPathElement(doc, refTag, nsPrefix);
    const bindingElement = this.createBindingElement(doc, part.model.id, xpath, nsPrefix);
    pathElement.appendChild(bindingElement);
    doc.documentElement.appendChild(pathElement);

    await part.dopeXPaths.setXml(doc.outerHTML);
  }

  private async addToConditionalPart(part: INamespacePart, refTag: string, conditionTag: string): Promise<void> {
    const conditionalPart = await this.getDOM(part.dopeConditions);
    const conditionElement = this.createConditionalElement(conditionalPart, refTag, conditionTag, 'od');
    conditionalPart.documentElement.appendChild(conditionElement);
    await part.dopeConditions.setXml(conditionalPart.outerHTML);
  }

  private createPathElement(doc: Dom, refTag: string, nsPrefix: string): Element {
    const pathElement = doc.createElement(`${nsPrefix}:xpath`);
    pathElement.setAttribute('id', refTag);
    return pathElement;
  }

  private createConditionalElement(doc: Dom, refTag: string, conditionTag: string, nsPrefix: string): Element {
    const conditionElement = doc.createElement(`${nsPrefix}:condition`);
    conditionElement.setAttribute('id', conditionTag);
    this.addFieldToCondition(doc, conditionElement, nsPrefix, refTag);
    return conditionElement;
  }

  private addFieldToCondition(doc: Dom, conditionElement: Element, nsPrefix: string, refTag: string): void {
    const pathElement = doc.createElement(`${nsPrefix}:xpathref`);
    pathElement.setAttribute('id', refTag);
    conditionElement.appendChild(pathElement);
  }

  private createBindingElement(doc: Dom, modelPartId: string, xpath: XPath | XPathCondition, nsPrefix: string): Element {
    const bindingElement = doc.createElement(`${nsPrefix}:dataBinding`);
    bindingElement.setAttribute('storeItemID', modelPartId);
    bindingElement.setAttribute('xpath', xpath);
    return bindingElement;
  }

  private makeRefTag(refId: number): string {
    return `x${refId}`;
  }

  private async makeConditionalTag(): Promise<string> {
    const refId = await this.applicationStoreService.incrementRefId();
    const currentTag = `c${refId}`;
    return currentTag;
  }
}
